import { initDB } from "./indexedDB";
import type { IDBPDatabase } from 'idb';

/**
 * IndexedDB utility functions for voucher management
 */

// Store names
const VOUCHER_STORE = "vouchers";
const VOUCHER_HISTORY_STORE = "voucher_history";
const VOUCHER_TYPES_STORE = "voucher_types";
const VOUCHER_SETTINGS_STORE = "voucher_settings";

// Initialize the voucher database
export const initializeVoucherDatabase = async (): Promise<void> => {
  try {
    console.log("Initializing voucher database...");
    await initDB();
    
    // Initialize with mock data if needed (only for development)
    await initializeWithMockDataIfNeeded();
    
    console.log("Voucher database initialized successfully");
  } catch (error) {
    console.error("Failed to initialize voucher database:", error);
    throw error;
  }
};

// Initialize with mock data if needed
const initializeWithMockDataIfNeeded = async (): Promise<void> => {
  try {
    const db = await initDB();
    const tx = db.transaction(VOUCHER_TYPES_STORE, 'readonly');
    const store = tx.objectStore(VOUCHER_TYPES_STORE);
    const count = await store.count();
    await tx.done;

    if (count === 0) {
      console.log("No voucher types found, initializing with default data...");
      await initializeVoucherTypes();
      console.log("Default voucher data initialized successfully");
    }
  } catch (error) {
    console.error("Error checking/initializing mock data:", error);
    throw error;
  }
};

// Initialize default voucher types
const initializeVoucherTypes = async (): Promise<void> => {
  const voucherTypes = [
    { typeId: "food-aid", name: "Food Aid", description: "Vouchers for basic food supplies", maxAmount: 100 },
    { typeId: "school-supplies", name: "School Supplies", description: "Vouchers for school materials", maxAmount: 50 },
    { typeId: "healthcare", name: "Healthcare", description: "Vouchers for basic medical supplies", maxAmount: 120 },
    { typeId: "utility", name: "Utility Payments", description: "Vouchers for utility bill payments", maxAmount: 200 }
  ];
  
  try {
    const db = await initDB();
    const tx = db.transaction(VOUCHER_TYPES_STORE, 'readwrite');
    const store = tx.objectStore(VOUCHER_TYPES_STORE);
    
    for (const type of voucherTypes) {
      await store.add(type);
    }
    
    await tx.done;
    console.log("Default voucher types added successfully");
  } catch (error) {
    console.error("Error initializing voucher types:", error);
    throw error;
  }
};

// Initialize default voucher settings
const initializeVoucherSettings = async (): Promise<void> => {
  const settings = {
    id: "voucher-settings",
    dailyCap: 1000,
    weeklyCap: 5000,
    notifyOnAssignment: true,
    smsTemplate: "You have been assigned a {voucherType} voucher worth RM{amount}. Valid until {expiryDate}. Your voucher code is {voucherCode}.",
    emailTemplate: "Dear {customerName},\n\nYou have been assigned a {voucherType} voucher worth RM{amount}. This voucher is valid until {expiryDate}.\n\nYour voucher code is {voucherCode}.\n\nThank you,\nJiMATPOS Team"
  };
  
  try {
    const db = await initDB();
    const tx = db.transaction(VOUCHER_SETTINGS_STORE, 'readwrite');
    const store = tx.objectStore(VOUCHER_SETTINGS_STORE);
    await store.put(settings);
    await tx.done;
    console.log("Default voucher settings added");
  } catch (error) {
    console.error("Error initializing voucher settings:", error);
    throw error;
  }
};

// Voucher related interfaces
export interface Voucher {
  voucherId: string;
  customerId: string;
  customerName: string;
  icNumber: string;
  mobileNumber: string;
  memberType: "Customer" | "Coop Member";
  coopId?: string;
  voucherCode: string;
  voucherAmount: number;
  voucherType: string;
  status: "Active" | "Expired" | "Used" | "Pending";
  issueDate: number;
  expiryDate: number;
  assignedBy: string;
  notes?: string;
  tags?: string[];
  remainingBalance: number;
  lastModified: number;
  needsSync: boolean;
}

export interface VoucherHistory {
  transactionId?: number;
  voucherId: string;
  customerName: string;
  voucherCode: string;
  redemptionDate: number;
  redemptionAmount: number;
  products: string[];
  categories: string[];
  outlet: string;
  terminal: string;
  cashierName: string;
  status: "Partially Used" | "Fully Used" | "Expired";
  lastModified: number;
  needsSync: boolean;
}

export interface VoucherType {
  typeId: string;
  name: string;
  description: string;
  maxAmount: number;
  lastModified?: number;
  needsSync?: boolean;
}

export interface VoucherSettings {
  id: string;
  dailyCap: number;
  weeklyCap: number;
  notifyOnAssignment: boolean;
  smsTemplate: string;
  emailTemplate: string;
  lastModified?: number;
  needsSync?: boolean;
}

// Voucher operations
export const getAllVouchers = async (): Promise<Voucher[]> => {
  try {
    const db = await initDB();
    const tx = db.transaction(VOUCHER_STORE, 'readonly');
    const store = tx.objectStore(VOUCHER_STORE);
    const vouchers = await store.getAll();
    await tx.done;
    return vouchers || [];
  } catch (error) {
    console.error("Error getting all vouchers:", error);
    throw error;
  }
};

export const saveVoucher = async (voucher: Voucher): Promise<void> => {
  try {
    const db = await initDB();
    const tx = db.transaction(VOUCHER_STORE, 'readwrite');
    const store = tx.objectStore(VOUCHER_STORE);
    await store.put(voucher);
    await tx.done;
  } catch (error) {
    console.error("Error saving voucher:", error);
    throw error;
  }
};

export const getVoucherById = async (voucherId: string): Promise<Voucher | null> => {
  try {
    const db = await initDB();
    const tx = db.transaction(VOUCHER_STORE, 'readonly');
    const store = tx.objectStore(VOUCHER_STORE);
    const voucher = await store.get(voucherId);
    await tx.done;
    return voucher || null;
  } catch (error) {
    console.error("Error getting voucher by ID:", error);
    throw error;
  }
};

// Voucher History operations
export const saveVoucherHistory = async (history: VoucherHistory): Promise<void> => {
  try {
    const db = await initDB();
    const tx = db.transaction(VOUCHER_HISTORY_STORE, 'readwrite');
    const store = tx.objectStore(VOUCHER_HISTORY_STORE);
    await store.add(history);
    await tx.done;
  } catch (error) {
    console.error("Error saving voucher history:", error);
    throw error;
  }
};

export const getAllVoucherHistory = async (): Promise<VoucherHistory[]> => {
  try {
    const db = await initDB();
    const tx = db.transaction(VOUCHER_HISTORY_STORE, 'readonly');
    const store = tx.objectStore(VOUCHER_HISTORY_STORE);
    const history = await store.getAll();
    await tx.done;
    return history || [];
  } catch (error) {
    console.error("Error getting voucher history:", error);
    throw error;
  }
};

// Voucher Type operations
export const getAllVoucherTypes = async (): Promise<VoucherType[]> => {
  try {
    const db = await initDB();
    const tx = db.transaction(VOUCHER_TYPES_STORE, 'readonly');
    const store = tx.objectStore(VOUCHER_TYPES_STORE);
    const types = await store.getAll();
    await tx.done;
    return types || [];
  } catch (error) {
    console.error("Error getting voucher types:", error);
    throw error;
  }
};

export const saveVoucherType = async (voucherType: VoucherType): Promise<void> => {
  try {
    const db = await initDB();
    const tx = db.transaction(VOUCHER_TYPES_STORE, 'readwrite');
    const store = tx.objectStore(VOUCHER_TYPES_STORE);
    await store.put(voucherType);
    await tx.done;
  } catch (error) {
    console.error("Error saving voucher type:", error);
    throw error;
  }
};

// Voucher Settings operations
export const getVoucherSettings = async (): Promise<VoucherSettings | null> => {
  try {
    const db = await initDB();
    const tx = db.transaction(VOUCHER_SETTINGS_STORE, 'readonly');
    const store = tx.objectStore(VOUCHER_SETTINGS_STORE);
    const settings = await store.get('voucher-settings');
    await tx.done;
    return settings || null;
  } catch (error) {
    console.error("Error getting voucher settings:", error);
    throw error;
  }
};

export const saveVoucherSettings = async (settings: VoucherSettings): Promise<void> => {
  try {
    const db = await initDB();
    const tx = db.transaction(VOUCHER_SETTINGS_STORE, 'readwrite');
    const store = tx.objectStore(VOUCHER_SETTINGS_STORE);
    await store.put(settings);
    await tx.done;
  } catch (error) {
    console.error("Error saving voucher settings:", error);
    throw error;
  }
};

// Helper function to generate a random voucher code
export const generateVoucherCode = (): string => {
  const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Excluded characters that can be confused (I, O, 0, 1)
  let result = '';
  const charactersLength = characters.length;
  
  // Format: XXXX-XXXX-XXXX (12 characters in 3 groups of 4)
  for (let i = 0; i < 12; i++) {
    if (i === 4 || i === 8) {
      result += '-';
    }
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  
  return result;
};
